import React, { useEffect, useState, useMemo } from 'react';
import { FileText, Calendar, User, Activity, Shield, Building } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/Badge';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import { Skeleton } from '@/components/ui/Skeleton';
import { Column } from '@/components/ui';
import { ResponsiveTable as DataTable } from '@/components/ui/responsive-table';
import { useAuth } from '@/contexts/AuthContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useDataTableFilters, DataTableFilter, createColumnConfigHelper, applyFilters } from '@/components/data-table-filter';
import { getSystemAuditLogs, getOrganizationAuditLogs } from '@/services/auditLogService';

/**
 * Row shape, named to match what the API returns. This page previously used
 * `userId` / `userEmail`, which no writer has ever produced — every audit
 * entry carries `actorId` / `actorEmail` — so the User column rendered blank
 * even for the handful of rows it did manage to load.
 */
interface AuditLog {
  id: string;
  timestamp: Date;
  actorId: string;
  actorEmail: string;
  action: string;
  resourceType?: string;
  /** Full resource identifier, e.g. "flow/123". */
  resource?: string;
  organizationId?: string;
  impersonatedOrgId?: string;
  success?: boolean;
  errorMessage?: string;
  changes?: string[];
  metadata?: Record<string, any>;
}

const filterColumnHelper = createColumnConfigHelper<AuditLog>();

const filterColumnsConfig = [
  filterColumnHelper
    .text()
    .id('actorEmail')
    .accessor((row) => row.actorEmail)
    .displayName('User Email')
    .icon(User)
    .build(),
  filterColumnHelper
    .text()
    .id('action')
    .accessor((row) => row.action)
    .displayName('Action')
    .icon(Activity)
    .build(),
  filterColumnHelper
    .option()
    .id('actionType')
    .accessor((row) => {
      if (row.impersonatedOrgId || row.action.includes('admin')) return 'admin';
      if (row.action.includes('organization') || row.action.includes('org')) return 'org';
      if (row.action.includes('user') || row.action.includes('login')) return 'user';
      return 'other';
    })
    .displayName('Type')
    .icon(Shield)
    .options([
      { label: 'User Actions', value: 'user' },
      { label: 'Org Actions', value: 'org' },
      { label: 'Admin Actions', value: 'admin' },
      { label: 'Other', value: 'other' },
    ])
    .build(),
  filterColumnHelper
    .text()
    .id('resourceType')
    .accessor((row) => row.resourceType || '')
    .displayName('Resource Type')
    .icon(FileText)
    .build(),
] as const;

export function AuditLogsPage() {
  const { isSuperAdmin, activeOrganizationId } = useAuth();
  const fmt = useDateFormat();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { columns: filterColumns, filters, actions, strategy } = useDataTableFilters({
    strategy: 'client',
    data: logs,
    columnsConfig: filterColumnsConfig,
  });

  useEffect(() => {
    loadAuditLogs();
  }, [activeOrganizationId, isSuperAdmin]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Read through the REST API, not Firestore directly. The previous query
      // hit a `auditLogs` collection whose only writer is the client-side org
      // switcher — every server-side audit entry goes to `audit_logs` — so this
      // page showed view-as-organization events and nothing else.
      const raw = isSuperAdmin
        ? await getSystemAuditLogs({ limit: 500 })
        : activeOrganizationId
          ? await getOrganizationAuditLogs(activeOrganizationId, { limit: 500 })
          : [];

      setLogs(
        raw.map((log: any) => ({
          id: log.id,
          timestamp: log.timestamp instanceof Date ? log.timestamp : new Date(log.timestamp),
          actorId: log.actorId || '',
          actorEmail: log.actorEmail || '',
          action: log.action || '',
          resourceType: log.resourceType,
          resource: log.resource,
          organizationId: log.organizationId,
          impersonatedOrgId: log.impersonatedOrgId,
          success: log.success,
          errorMessage: log.errorMessage,
          changes: log.changes,
          metadata: log.metadata,
        })),
      );
    } catch (err: any) {
      console.error('Failed to load audit logs:', err);
      setError(err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionVariant = (action: string): { variant: 'default' | 'secondary' | 'outline' | 'destructive'; className?: string } => {
    if (action.includes('login') || action.includes('create')) return { variant: 'secondary', className: 'bg-status-success-bg text-status-success hover:bg-status-success-bg/80' };
    if (action.includes('delete') || action.includes('fail')) return { variant: 'destructive' };
    if (action.includes('impersonate') || action.includes('admin')) return { variant: 'outline', className: 'border-status-warning text-status-warning' };
    return { variant: 'default' };
  };

  const filteredLogs = useMemo(() =>
    applyFilters(logs, filters, filterColumnsConfig),
    [logs, filters]
  );

  // Calculate statistics
  const stats = {
    total: logs.length,
    user: logs.filter(l => l.action.includes('user') || l.action.includes('login')).length,
    org: logs.filter(l => l.action.includes('organization') || l.action.includes('org')).length,
    admin: logs.filter(l => l.impersonatedOrgId || l.action.includes('admin')).length,
  };

  // Define table columns
  const columns: Column<AuditLog>[] = [
    {
      key: 'timestamp',
      header: 'Timestamp',
      sortable: true,
      render: (_, log) => (
        <div className="text-sm">
          <div className="font-medium text-foreground">
            {fmt.formatTime(log.timestamp)}
          </div>
          <div className="text-muted-foreground/70">
            {fmt.formatDate(log.timestamp)}
          </div>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      sortable: true,
      render: (_, log) => {
        const actionStyle = getActionVariant(log.action);
        return (
          <div className="flex items-center gap-2">
            <Badge variant={actionStyle.variant} className={actionStyle.className}>
              {log.action}
            </Badge>
            {log.impersonatedOrgId && (
              <Badge variant="outline" className="border-status-warning text-status-warning">
                <Shield className="w-3 h-3 mr-1" />
                Impersonated
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: 'actorEmail',
      header: 'User',
      sortable: true,
      render: (_, log) => (
        <div className="text-sm">
          <div className="font-medium text-foreground">
            {log.actorEmail || <span className="text-muted-foreground/70">system</span>}
          </div>
          {log.actorId && (
            <div className="text-muted-foreground/70 font-mono text-xs">
              {log.actorId.substring(0, 8)}...
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'resourceType',
      header: 'Resource',
      render: (_, log) => (
        log.resourceType ? (
          <div className="text-sm">
            <div className="font-medium text-foreground">
              {log.resourceType}
            </div>
            {log.resource && (
              <div className="text-muted-foreground/70 font-mono text-xs">
                {log.resource}
              </div>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground/70">-</span>
        )
      ),
    },
    {
      key: 'success',
      header: 'Result',
      render: (_, log) => (
        log.success === false ? (
          <div className="text-sm">
            <Badge variant="destructive">Failed</Badge>
            {log.errorMessage && (
              <div className="text-muted-foreground/70 text-xs mt-1 max-w-xs truncate" title={log.errorMessage}>
                {log.errorMessage}
              </div>
            )}
          </div>
        ) : (
          <Badge variant="outline">OK</Badge>
        )
      ),
    },
  ];

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2">
              Audit Logs
            </h1>
            <p className="text-body text-muted-foreground">
              {isSuperAdmin ? "System-wide audit trail of all actions" : "Your organization's audit trail"}
            </p>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Card className="p-6">
            <div className="space-y-2">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-12 w-12" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Stats */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-accent-blue" />
                <div>
                  <div className="text-caption text-muted-foreground uppercase">Total Logs</div>
                  <div className="text-h2 font-mono font-bold text-foreground">{stats.total}</div>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-accent-green" />
                <div>
                  <div className="text-caption text-muted-foreground uppercase">User Actions</div>
                  <div className="text-h2 font-mono font-bold text-accent-green">{stats.user}</div>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <Building className="w-5 h-5 text-accent-purple" />
                <div>
                  <div className="text-caption text-muted-foreground uppercase">Org Actions</div>
                  <div className="text-h2 font-mono font-bold text-accent-purple">{stats.org}</div>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-accent-orange" />
                <div>
                  <div className="text-caption text-muted-foreground uppercase">Admin Actions</div>
                  <div className="text-h2 font-mono font-bold text-accent-orange">{stats.admin}</div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Audit Logs Table */}
        {!loading && (
          <Card className="p-6">
            <div className="mb-4 pb-4 border-b border-border">
              <DataTableFilter
                columns={filterColumns}
                filters={filters}
                actions={actions}
                strategy={strategy}
              />
            </div>
            <DataTable
              columns={columns}
              data={filteredLogs}
              keyExtractor={(log) => log.id}
              searchable
              searchPlaceholder="Search logs by action, user, or resource..."
              paginated
              defaultPageSize={25}
              emptyMessage="No audit logs found"
            />
          </Card>
        )}
      </div>
    </AppShell>
  );
}
